import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';
import { getActiveTenantId } from '@/lib/tenant';
import type {
  AgentCatalog,
  Catalog,
  CreateCatalogInput,
  CreateProductInput,
  Product,
  ProductListParams,
  ProductListResponse,
  UpdateCatalogInput,
  UpdateProductInput,
} from '@/lib/api/types';

const catalogKeys = {
  all: () => ['catalogs', getActiveTenantId()] as const,
  // The detail key is by `ref` -- a slug or a UUID (spec 0013, AC-9).
  detail: (ref: string) => ['catalogs', getActiveTenantId(), ref] as const,
};

export function useCatalogs() {
  return useQuery({
    queryKey: catalogKeys.all(),
    queryFn: () => apiFetch<Catalog[]>('/catalogs'),
  });
}

/** Resolve a catalog by `ref` -- its slug or UUID (spec 0013, ADR-0002). */
export function useCatalog(ref: string | undefined) {
  return useQuery({
    queryKey: ref ? catalogKeys.detail(ref) : ['catalogs', 'detail', 'none'],
    queryFn: () => apiFetch<Catalog>(`/catalogs/${ref}`),
    enabled: Boolean(ref),
  });
}

export function useCreateCatalog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCatalogInput) =>
      apiFetch<Catalog>('/catalogs', { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['catalogs'] });
    },
  });
}

export function useUpdateCatalog(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateCatalogInput) =>
      apiFetch<Catalog>(`/catalogs/${id}`, { method: 'PATCH', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['catalogs'] });
    },
  });
}

export function useDeleteCatalog() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiFetch<void>(`/catalogs/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['catalogs'] });
    },
  });
}

// ----- Products -----

function productsKey(catalogId: string | undefined, params: ProductListParams) {
  return ['products', getActiveTenantId(), catalogId, params] as const;
}

export function useProducts(catalogId: string | undefined, params: ProductListParams = {}) {
  return useQuery({
    queryKey: productsKey(catalogId, params),
    queryFn: () =>
      apiFetch<ProductListResponse>(
        `/catalogs/${catalogId}/products?${new URLSearchParams(
          Object.entries(params)
            .filter(([, v]) => v !== undefined && v !== '')
            .map(([k, v]) => [k, String(v)]),
        ).toString()}`,
      ),
    enabled: Boolean(catalogId),
  });
}

export function useCreateProduct(catalogId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductInput) =>
      apiFetch<Product>(`/catalogs/${catalogId}/products`, { method: 'POST', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products', getActiveTenantId(), catalogId] });
      void queryClient.invalidateQueries({ queryKey: ['catalogs'] });
    },
  });
}

export function useUpdateProduct(catalogId: string, productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProductInput) =>
      apiFetch<Product>(`/products/${productId}`, { method: 'PATCH', body: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products', getActiveTenantId(), catalogId] });
    },
  });
}

export function useDeleteProduct(catalogId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) =>
      apiFetch<void>(`/products/${productId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['products', getActiveTenantId(), catalogId] });
      void queryClient.invalidateQueries({ queryKey: ['catalogs'] });
    },
  });
}

// ----- Agent links -----

function agentCatalogsKey(agentId: string | undefined) {
  return ['agent-catalogs', getActiveTenantId(), agentId] as const;
}

export function useAgentCatalogs(agentId: string | undefined) {
  return useQuery({
    queryKey: agentCatalogsKey(agentId),
    queryFn: () => apiFetch<AgentCatalog[]>(`/agents/${agentId}/catalogs`),
    enabled: Boolean(agentId),
  });
}

export function useAttachAgentCatalog(agentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (catalogId: string) =>
      apiFetch<AgentCatalog>(`/agents/${agentId}/catalogs`, {
        method: 'POST',
        body: { catalog_id: catalogId },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentCatalogsKey(agentId) });
    },
  });
}

/** Flip an assignment's write grant (spec 0017). Granting is owner/admin-only
 * server-side; the panel disables the control for members. */
export function useSetAgentCatalogWritable(agentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ catalogId, writable }: { catalogId: string; writable: boolean }) =>
      apiFetch<AgentCatalog>(`/agents/${agentId}/catalogs/${catalogId}`, {
        method: 'PATCH',
        body: { writable },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentCatalogsKey(agentId) });
    },
  });
}

export function useDetachAgentCatalog(agentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (catalogId: string) =>
      apiFetch<void>(`/agents/${agentId}/catalogs/${catalogId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentCatalogsKey(agentId) });
    },
  });
}
